import { useEffect, useRef, useState } from "react";

/**
 * One-shot scroll reveal. Flips `inView` true the first time the element is in
 * view, then stops. Primary path is IntersectionObserver; a scroll/resize +
 * on-mount check is layered on as a fallback because some in-app browsers
 * (LINE / LinkedIn WebViews) don't always fire IO callbacks — without the
 * fallback the `.lp-reveal` content could stay at opacity 0 (blank sections).
 * Reduced motion collapses the CSS to final states, so firing early is safe.
 */
export function useReveal<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let done = false;

    const check = () => {
      if (done) return;
      const r = el.getBoundingClientRect();
      if (r.top < window.innerHeight * 0.92 && r.bottom > 0) reveal();
    };

    let io: IntersectionObserver | null = null;

    function reveal() {
      if (done) return;
      done = true;
      setInView(true);
      io?.disconnect();
      window.removeEventListener("scroll", check);
      window.removeEventListener("resize", check);
    }

    if (typeof IntersectionObserver !== "undefined") {
      io = new IntersectionObserver(
        (entries) => {
          for (const e of entries) if (e.isIntersecting) reveal();
        },
        { threshold: 0.2, rootMargin: "0px 0px -10% 0px" },
      );
      io.observe(el);
    }
    window.addEventListener("scroll", check, { passive: true });
    window.addEventListener("resize", check);
    check(); // reveal already-in-view content even if IO stays silent

    return () => {
      io?.disconnect();
      window.removeEventListener("scroll", check);
      window.removeEventListener("resize", check);
    };
  }, []);

  return { ref, inView };
}
